import React, {useCallback} from 'react'
import {useDispatch} from 'react-redux'
import {
  setLightboxOverlayVisibility,
  setLightboxSelectedImage
} from 'modules/accommodation/slice'
import {isEmpty} from 'ramda'
import {imageProvider} from 'utils/imageProvider'

import {Icon} from '@daedalus/atlas/Icon'
import {Image} from '@daedalus/atlas/Image'
import {useDispatchTeamEvent} from '@daedalus/core/src/analytics/components/TeamContext'
import {trackEvent} from '@daedalus/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity
} from '@daedalus/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage'

import {GridGalleryLoading} from './GridGalleryLoading'
import {getImageSize} from './helpers'
import {AllPhotosButton, Grid, GridItem, Wrapper} from './styles'

export const BASE_NUM_IMAGES = 5

interface Props {
  images: string[]
  isLoading?: boolean
}

export const GridGallery: React.FC<Props> = ({images, isLoading = false}) => {
  const dispatch = useDispatch()
  const dispatchTeamEvent = useDispatchTeamEvent()
  const imagesToRender = images?.slice(0, BASE_NUM_IMAGES) ?? []
  const numImagesInGrid = imagesToRender.length
  const numTotalImages = images?.length

  const handleImageClick = useCallback(
    (selectedImage?: string) => {
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.HotelDetailsGallery,
          action: Action.Clicked
        })
      )
      dispatch(setLightboxSelectedImage(selectedImage))
      dispatch(setLightboxOverlayVisibility(true))
    },
    [dispatch, dispatchTeamEvent]
  )

  if (isLoading) return <GridGalleryLoading />

  if (isEmpty(images)) return null

  return (
    <Wrapper data-testid="grid-gallery">
      <Grid className={`__${numImagesInGrid}-images`}>
        {imagesToRender.map((image, index) => (
          <GridItem
            key={index}
            data-id={`gallery-${index}`}
            role="button"
            onClick={() => handleImageClick(image)}
          >
            <Image
              url={image}
              imageProvider={imageProvider}
              size={getImageSize(numImagesInGrid, index)}
              isResponsive
            />
          </GridItem>
        ))}
      </Grid>

      <AllPhotosButton
        variant="secondary"
        iconStart={<Icon name="Photo" />}
        onClick={() => handleImageClick(imagesToRender[0])}
      >
        <FormattedMessageWrapper
          defaultMessage="All photos ({numTotalImages})"
          id="allPhotosWithNumber"
          values={{
            numTotalImages
          }}
        />
      </AllPhotosButton>
    </Wrapper>
  )
}
